import { logger } from '@/lib/logger'

/**
 * LucideIcon Component
 * Dynamically renders Lucide icons by string name
 *
 * This allows icons to be specified from the server as strings,
 * making it perfect for dynamic content like notifications, alerts, etc.
 *
 * OPTIMIZED: Only imports icons that are actually used in the app
 * to avoid bundling all 1400+ lucide icons.
 *
 * Usage:
 * ```tsx
 * <LucideIcon name="Bell" />
 * <LucideIcon name="Mail" size={24} className="text-blue-500" />
 * <LucideIcon name="CheckCircle" strokeWidth={2.5} />
 * ```
 */

import type { LucideProps } from 'lucide-react'
import {
  // Navigation icons
  Home,
  PlusCircle,
  CalendarDays,
  Calendar,
  Bell,
  CreditCard,
  Settings,
  Package,
  SquareLibrary,
  // Notification icons
  DollarSign,
  CheckCircle,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  // Common UI icons
  Mail,
  User,
  LogOut,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  X,
  Plus,
  Minus,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  Search,
  Filter,
  MoreVertical,
  MoreHorizontal,
  ExternalLink,
  Link,
  Copy,
  Share,
  Download,
  Upload,
  Image,
  FileText,
  Loader,
  Loader2,
  PackageCheck,
  PartyPopper,
  RefreshCw,
  Clock,
  MapPin,
  Phone,
  Globe,
  Info,
  HelpCircle,
  Star,
  Heart,
  Send,
  MessageSquare,
  Users,
  UserPlus,
  UserCheck,
  Sparkles,
  Zap,
  Award,
  Gift,
  Wallet,
  Receipt,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ArrowDown,
  Check,
  Coins,
  Rocket,
} from 'lucide-react'

// Map of icon names to components
const ICON_MAP: Record<string, React.ComponentType<LucideProps>> = {
  // Navigation
  Home,
  PlusCircle,
  CalendarDays,
  Calendar,
  Bell,
  CreditCard,
  Settings,
  Package,
  SquareLibrary,
  // Notifications
  DollarSign,
  CheckCircle,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  // Common UI
  Mail,
  User,
  LogOut,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  X,
  Plus,
  Minus,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  Search,
  Filter,
  MoreVertical,
  MoreHorizontal,
  ExternalLink,
  Link,
  Copy,
  Share,
  Download,
  Upload,
  Image,
  FileText,
  Loader,
  Loader2,
  PackageCheck,
  PartyPopper,
  RefreshCw,
  Clock,
  MapPin,
  Phone,
  Globe,
  Info,
  HelpCircle,
  Star,
  Heart,
  Send,
  MessageSquare,
  Users,
  UserPlus,
  UserCheck,
  Sparkles,
  Zap,
  Award,
  Gift,
  Wallet,
  Receipt,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ArrowDown,
  Check,
  Coins,
  Rocket,
}

export interface LucideIconProps extends Omit<LucideProps, 'ref'> {
  name: string
  fallback?: React.ReactNode
}

export function LucideIcon({
  name,
  fallback = null,
  ...props
}: LucideIconProps) {
  // Get the icon component from our map
  const IconComponent = ICON_MAP[name]

  // If icon doesn't exist in our map, render fallback
  if (!IconComponent) {
    if (process.env.NODE_ENV === 'development') {
      logger.warn(
        `[LucideIcon] Icon "${name}" not found. Add it to ICON_MAP in LucideIcon.tsx`
      )
    }
    return <>{fallback}</>
  }

  return <IconComponent {...props} />
}

// Export type for icon names
export type LucideIconName = keyof typeof ICON_MAP
